import { AfterViewInit, Component, OnDestroy, OnInit, QueryList, ViewChild, ViewChildren, ViewEncapsulation, inject, DestroyRef, ChangeDetectionStrategy } from '@angular/core';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatTable } from '@angular/material/table';
import { ModalComponent } from '@appkit4/angular-components/modal';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { StmtBtnActionPayload } from '../../shared/components/stmt-id-btn/stmt-id-btn.component';
import { DSTStatement } from '../../shared/models/dst-statement.model';
import { DiscModalViewerComponent } from '../../shared/components/disc-modal-viewer/disc-modal-viewer.component';
import { TrackerTabsService } from '../../statement-tracker/tracker-tabs.service';
import { Alert } from '../../statement-tracker/statement-tracker.service';
import { ProjectTaggingComponent } from '../../shared/components/project-tagging/project-tagging.component';
import { CopyReplicateComponent } from '../../statement-tracker/copy-replicate/copy-replicate.component';
import { SaveActionResponse } from '../../../shared/_models/save-action-response.model';
import { HttpErrorResponse } from '@angular/common/http';
import { CustomReportService, DisclosureByTypesResponse } from './custom-report.service';
import { CreateDisclosureService, DSTTaxYear } from '../../statement-tracker/create-disclosure/create-new/create-disclosure.service';
import { BehaviorSubject, debounceTime, Subscription, take, zip } from 'rxjs';
import { DstMatTableDataSource } from '../../shared/mat-table/dst-mat-table-data-source';
import { DstMatTableFilterApi } from '../../shared/mat-table/dst-mat-table-filter-api';
import { connectMatTableSort, getDstColStyle } from '../../shared/mat-table/dst-mat-grid.util';
import { JSONExportHeader, JSONExportService } from '../../../shared/_services/JSONExport.service';
import { DfMeDropdownComponent } from '../../../shared/components/df-me-dropdown/df-me-dropdown.component';
import { DSTDynamicComponentResolverService } from '../../statement-tracker/create-disclosure/shared/dst-dynamic-component-resolver.service';
import { PtableColConfigModel } from '../../shared/models/ptable-col-config.model';
import { DSTSharedService } from '../../shared/dst-shared.service';
import { StmtIdBtnService } from '../../shared/components/stmt-id-btn/stmt-id-btn.service';
import { DSTChildFormState } from '../../shared/models/dst-child-form-state.model';
import * as _ from 'lodash-es';
import { GlobalService } from '../../../global/services/global.service';
import { WorkspaceService } from '../../../workspace/workspace.service';
import { AppService } from '../../../app.service';
import {
  generalConfigFromGlobal,
  hydratedGlobalParams,
} from '../../../shared/workspace-element-context';
import {
  getAppkitDropdownSelection,
  mapToAppkitDropdownItems,
  setDropdownAriaLabel
} from '../../../shared/utils/appkit-dropdown.util';

import { safeJsonInput } from '../../../shared/_utils/safe-json-input';
@Component({
  selector: 'dst-custom-report',
  templateUrl: './custom-report.component.html',
  styleUrls: [
    '../../shared/styles/dst-toolbar.scss',
    '../../shared/mat-table/dst-mat-table.scss',
    './custom-report.component.scss'
  ],
  encapsulation: ViewEncapsulation.None,
  standalone: false,
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class CustomReportComponent implements OnInit, AfterViewInit, OnDestroy {
  private readonly destroyRef = inject(DestroyRef);

  private contextApplied = false;

  @ViewChild(MatPaginator) paginator!: MatPaginator;
  @ViewChild(MatSort) sort!: MatSort;
  @ViewChild(MatTable) matTable!: MatTable<DSTStatement>;
  @ViewChild('customReportFilterModal') customReportFilterModal!: ModalComponent;
  @ViewChild(DiscModalViewerComponent) modalViewerComponent!: DiscModalViewerComponent;
  @ViewChild(ProjectTaggingComponent) projectTaggingComponent!: ProjectTaggingComponent;
  @ViewChild(CopyReplicateComponent) copyRepComponent!: CopyReplicateComponent;
  @ViewChildren(DfMeDropdownComponent) dfMeDropdownComponents!: QueryList<DfMeDropdownComponent>;
  @ViewChild(DfMeDropdownComponent) dfMeDropdownComponent!: DfMeDropdownComponent;

  showProjectTaggingModal: boolean = false;
  showCopyRepModal: boolean = false;
  // Fixed page size — paginator shows 8 rows per page regardless of viewport.
  // We used to auto-calculate this from `tbodyMaxHeight`, but the calc reads
  // the container height in `ngAfterViewInit` BEFORE data / Tax Year dropdown
  // are loaded, so the container is often still small and we'd end up with
  // `rows = 1`. Fixed value is more predictable and matches the spec.
  rows: number = 8;
  data: any[] = [];
  dataSource = new DstMatTableDataSource<DSTStatement>([]);
  table!: DstMatTableFilterApi<DSTStatement>;
  displayedColumns: string[] = [];
  filterColumns: string[] = [];
  childDisplayedColumns: string[] = [];
  expandedElement: DSTStatement | null = null;
  exportingExcel = false;
  excelExportFileName: string;
  getDstColStyle = getDstColStyle;
  cols: PtableColConfigModel[] = [
    {
      field: 'statementId',
      header: 'Stmt ID',
      width: '120px'
    },
    {
      field: 'TAXYEAR',
      header: 'Year',
      width: '130px'
    },
    {
      field: 'formName',
      header: 'Disclosure',
      width: '180px'
    },
    {
      field: 'disclosureType',
      header: 'Disclosure Type',
      width: '150px'
    },
    {
      field: 'primaryBusiness',
      header: 'Business',
      width: '280px'
    },
    {
      field: 'secondaryBusiness',
      header: 'Secondary Business',
      width: '0px'
    },
    {
      field: 'primaryLeid',
      header: 'LEID',
      width: '80px'
    },
    {
      field: 'secondaryLeid',
      header: 'Secondary LEID',
      width: '0px'
    },
    {
      field: 'primaryEntity',
      header: 'Entity Name',
      width: '300px'
    },
    {
      field: 'secondaryEntity',
      header: 'Secondary Entity Name',
      width: '0px'
    },
    {
      field: 'branchLeid',
      header: 'Branch Leid',
      width: '0px'
    },
    {
      field: 'createdBy',
      header: 'Created By',
      width: '0px'
    },
    {
      field: 'modifiedBy',
      header: 'Modifed By',
      width: '0px'
    },
    {
      field: 'createdOn',
      header: 'Created On',
      width: '0px'
    },
    {
      field: 'modifiedOn',
      header: 'Modified On',
      width: '0px'
    },
    {
      field: 'FilingGroup',
      header: 'Filing Group',
      width: '360px'
    },
    {
      field: 'FilingGroupReturnType',
      header: 'Filing Group Return Type',
      width: '60px'
    },
    {
      field: 'status',
      header: 'Status',
      width: '80px'
    },
    {
      field: 'isQar',
      header: 'Is QAR',
      width: '0px'
    },
    {
      field: 'projectName',
      header: 'Project',
      width: '280px'
    },
    {
      field: 'projectStepName',
      header: 'Project Steps',
      width: '0px'
    },
    {
      field: 'projectBslaName',
      header: 'Project BSLA',
      width: '0px'
    },
    {
      field: 'ignoredId', //
      header: 'Diagnostics Status',
      width: '180px'
    },

    {
      field: 'signatureFlag',
      header: 'Signature Flag',
      width: '180px'
    },
    {
      field: 'electionFlag',
      header: 'Election Flag',
      width: '180px'
    },
    {
      field: 'userActivity',
      header: 'User Activity',
      width: '300px'
    }
  ];
  childCols: any[] = [
    {
      field: 'statementId',
      header: 'Stmt ID'
    },
    {
      field: 'TAXYEAR',
      header: 'Year'
    },
    {
      field: 'formName',
      header: 'Disclosure'
    },
    {
      field: 'disclosureType',
      header: 'Disclosure Type',
    },
    {
      field: 'primaryBusiness',
      header: 'Business'
    },
    {
      field: 'primaryLeid',
      header: 'LEID'
    },
    {
      field: 'primaryEntity',
      header: 'Entity Name'
    },
    {
      field: 'FilingGroup',
      header: 'Filing Group'
    },
    {
      field: 'FilingGroupReturnType',
      header: 'Filing Group Return Type'
    },
    {
      field: 'createdBy',
      header: 'User Activity'
    },
    {
      field: 'status',
      header: 'Status'
    },
    {
      field: 'projectName',
      header: 'Project'
    }
  ];
  disclosureTypes: DisclosureByTypes[] = [];
  allDisclosures: Disclosure[] = [];
  taxYears: DSTTaxYear[] = [];
  fStatusList: any[] = [{ value: '', name: 'All' }, { value: '1', name: 'Filed' }, { value: '0', name: 'Not Filed' }];
  QARStatusList: any[] = [{ value: '', name: 'All' }, { value: '1', name: 'Qar' }, { value: '0', name: 'Not Qar' }];
  sigElecFlagList: any[] = [{ value: '', name: 'All' }, { value: '1', name: 'Yes' }, { value: '0', name: 'No' }];
  selectedTaxYearsObj: DSTTaxYear[] = [];
  selectedDisclosuresObj: Disclosure[] = [];
  selectedFStatus: any = this.fStatusList[0];
  selectedQARStatus: any = this.QARStatusList[0];
  selectedSigFlag: any = this.sigElecFlagList[0];
  selectedElecFlag: any = this.sigElecFlagList[0];
  fStatusListItems: any[] = mapToAppkitDropdownItems(this.fStatusList, 'name', 'value');
  QARStatusListItems: any[] = mapToAppkitDropdownItems(this.QARStatusList, 'name', 'value');
  sigElecFlagListItems: any[] = mapToAppkitDropdownItems(this.sigElecFlagList, 'name', 'value');
  selectedFStatusItem: any = this.fStatusListItems[0];
  selectedQARStatusItem: any = this.QARStatusListItems[0];
  selectedSigFlagItem: any = this.sigElecFlagListItems[0];
  selectedElecFlagItem: any = this.sigElecFlagListItems[0];
  fStatementId: string = '';
  fFormName: string = '';
  fDisclosureType: string = '';
  fPrimaryLeid: string = '';
  fPrimaryEntity: string = '';
  fFilingGroup: string = '';
  fFilingGroupReturnType: string = '';
  fCreatedBy: string = '';
  fStatus: string = '';
  fProjectName: string = '';
  fSignatureFlag: string = '';
  fElectionFlag: string = '';
  loading: boolean = false;
  showModalViewer: boolean = false;
  showGridFilters: boolean = false;
  taxyearLoaded: boolean = false;
  // ap-dropdown (Appkit4) needs `{value, label}` items; we keep a transformed
  // copy of `taxYears` (DSTTaxYear[]) for it. `selectedTaxYearItem` mirrors
  // ap-dropdown's ngModel; `taxYear` keeps the original DSTTaxYear object used
  // by the rest of the loading/refresh logic.
  taxYearItems: { value: number; label: string }[] = [];
  private taxYearItemsAll: { value: number; label: string }[] = [];
  selectedTaxYearItem: any = {};
  // Appkit's multi-select renders the option list itself, reading the checkbox
  // and the accessibility role off each item, and it does not narrow its own
  // list while searching: it emits the matches and expects them to be bound
  // back. Each multi-select therefore gets its own display list, keyed here so
  // the two tax-year dropdowns can be searched independently.
  dropdownOptions: { [key: string]: any[] } = {
    taxYearColumn: [],
    taxYearPanel: [],
    disclosure: []
  };
  // Full option lists. Search results are copied into `dropdownOptions`; the
  // source stays intact so clearing the search box restores every option.
  private dropdownSource: { [key: string]: any[] } = {
    taxYearColumn: [],
    taxYearPanel: [],
    disclosure: []
  };
  activeRow!: DSTStatement;
  copyMode: string = '';
  alert: Alert;

  private selectedTaxYears: string = '';
  private selectedMes: string = '';
  private selectedDisclosures: string = '';
  private onDisCancelSubs!: Subscription;
  taxYear: DSTTaxYear | undefined;
  private selectedCustomMes: string = '';
  statementFilterCriteria: string = '';
  private statementFilter = new BehaviorSubject<string | null>(null);
  statementFilterSubscription: Subscription;
  multipleTaxYears: string = '';

  constructor(private dstSharedService: DSTSharedService,
    private stmtIdBtnService: StmtIdBtnService,
    private trackerTabsService: TrackerTabsService,
    private cReportService: CustomReportService,
    private cDSerivce: CreateDisclosureService,
    private dCRService: DSTDynamicComponentResolverService,
    private jsonExportService: JSONExportService,
    private gtwGlobal: GlobalService,
    private gtwWorkspace: WorkspaceService,
    private gtwApp: AppService
  ) {
    this.excelExportFileName = 'Reports-Custom_Report_' + this.dstSharedService.getExcelExportDateString();
    this.displayedColumns = this.visibleCols.map((c) => c.field);
    this.filterColumns = this.visibleCols.map((c) => `${c.field}_filter`);
    // Nested mat-table column ids must not match the parent. In production a
    // duplicate name replaces the parent column and its header renders blank.
    this.childDisplayedColumns = this.childCols.map((c) => `child_${c.field}`);
    this.table = new DstMatTableFilterApi(this.dataSource);
    // local test only
    // this.dstSharedService.setBaseURLs({
    //   api: '/gtw',
    //   custom: '/custom',
    //   admin: '/admin',
    //   efile: '/gtw-efile-api',
    //   pdfEngine: '/pdf-engine',
    //   bulkPdfEngine: '/bulk-pdf-engine'
    // });
    // this.dstSharedService.setClientKey('92');
    // this.dstSharedService.setDSTUser('400396761', 5);
    // this.dstSharedService.setS3uploadButtonAccessUsers(['400396761']);
    // this.dstSharedService.setTaxYearScenario('2022', '52');
    // this.selectedTaxYears = '2022';
    // // end of local test only
    this.alert = dstSharedService.alert;

    this.statementFilterSubscription = this.statementFilter.pipe(debounceTime(600)).subscribe((val: string | null) => {
      if (val === null) {
        return;
      }

      this.filterCol({ target: { value: val } }, 'statementId', 'contains');

      setTimeout(() => {
        if (!this.table.filteredValue || this.table.filteredValue.length === 0) {
          this.statementFilterCriteria = val;
          this.refresh();
        }
      });
    });

    this.table.onFilter.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.expandedElement = null;
    });
  }

  ngOnInit(): void {
    window.onresize = () => {
      this.viewportReady();
    };

    // this.populateTestData();
    // local test only
    // this.data = [];
    // this.loading = true;
    // const getTaxYears$ = this.cDSerivce.getTaxYears();
    // const getDisclosures$ = this.cReportService.loadDisclosures();
    // zip(getTaxYears$, getDisclosures$).pipe(take(1)).subscribe((data: [DSTTaxYear[], DisclosureByTypesResponse[]]) => {
    //   this.taxYears = data[0];
    //   this.setTaxYear('2022');
    //   try {
    //     data[1].forEach((d: DisclosureByTypesResponse) => {
    //       const disclosureType = safeJsonInput<any>(d.DISCLOSURETYPES, null, 'custom-report.parse1');
    //       this.disclosureTypes.push(disclosureType);
    //       this.allDisclosures = _.uniqBy(this.allDisclosures.concat(disclosureType.children), "formId");
    //     });
    //   } catch (e: any) {
    //     if (e instanceof SyntaxError) {
    //       console.log('JSON parse Syntax Error' + e);
    //     } else {
    //       console.error(e);
    //     }
    //   }
    //   this.refresh();
    // });
    // end of local test only
    this.onDisCancelSubs = this.dCRService.onDisclosureCancel.subscribe(() => {
      this.trackerTabsService.destroyModalTab();
      this.showModalViewer = false;
      this.showCopyRepModal = false;
    });

    this.applyWorkspaceContext();
    this.onDisCancelSubs.add(
      this.gtwGlobal.globalParamsChanged$.subscribe(() => {
        this.applyWorkspaceContext();
      })
    );
  }

  private applyWorkspaceContext(): void {
    if (this.contextApplied) return;
    try {
      const gc = generalConfigFromGlobal(this.gtwGlobal);
      const gp = hydratedGlobalParams(this.gtwGlobal, this.gtwWorkspace);
      const user = this.gtwApp.USER_SETTINGS?.user || {};
      const clientKey = String(user.client?.client_key || user.client_key || '');
      const ssoId = String(user.sso_id || '');
      // See DST_MODULE_KNOWLEDGE.md §5. activeScreen.parent is undefined in the
      // new workspace; fall back to USER_SETTINGS fields so dropdown menu
      // actions (Copy, Delete, Replicate, ...) actually show.
      const roleLevel = Number(
        user.system_access_level
          ?? user.user_type_key
          ?? user.access_level
          ?? this.gtwWorkspace.workspaceFactory?.activeScreen?.parent?.access_level
          ?? 0
      );
      const taxYear = String(gp.tax_year || '');
      const scenario = String(gp.scenario || '');

      if (!clientKey && !ssoId) return;
      this.contextApplied = true;

      this.dstSharedService.setClientKey(clientKey);
      this.dstSharedService.setDSTUser(ssoId, roleLevel);
      this.dstSharedService.setSystemLockUnlockStatus(false, roleLevel);
      this.dstSharedService.setTaxYearScenario(taxYear, scenario);
      this.dstSharedService.setBaseURLs(gc);

      this.selectedTaxYears = this.dstSharedService.gtwTaxYear;
      this.disclosureTypes = [];
      this.allDisclosures = [];
      this.data = [];
      this.loading = true;
      const getTaxYears$ = this.cDSerivce.getTaxYears();
      const getDisclosures$ = this.cReportService.loadDisclosures();
      zip(getTaxYears$, getDisclosures$).pipe(take(1)).subscribe((data: [DSTTaxYear[], DisclosureByTypesResponse[]]) => {
        this.taxYears = data[0];
        this.setTaxYear(taxYear);

        // this.dfMeDropdownComponent.loadMEsCustom(this.taxYear?.text.toString()!);
        try {
          data[1].forEach((d: DisclosureByTypesResponse) => {
            const disclosureType = safeJsonInput<any>(d.DISCLOSURETYPES, null, 'custom-report.parse2');
            this.disclosureTypes.push(disclosureType);
            this.allDisclosures = _.uniqBy(this.allDisclosures.concat(disclosureType.children), "formId");
          });
          this.publishDropdownOptions('disclosure', this.allDisclosures);
        } catch (e: any) {
          if (e instanceof SyntaxError) {
            console.log('JSON parse Syntax Error' + e);
          } else {
            console.error(e);
          }
        }
        this.refresh();
      });
    } catch (e) {
      this.dstSharedService.showAlert('error', 'Missing context, please contact system administrator for assistance!');
    }
  }

  ngAfterViewInit(): void {
    this.connectTableControls();
    this.viewportReady();
  }

  private connectTableControls(): void {
    if (this.paginator) {
      this.dataSource.paginator = this.paginator;
    }
    connectMatTableSort(this.dataSource, this.sort);
  }

  get pageIndex(): number {
    return this.paginator?.pageIndex ?? 0;
  }

  get pageCount(): number {
    const size = this.paginator?.pageSize || this.rows || 1;
    const length = this.paginator?.length ?? this.data?.length ?? 0;
    return Math.max(1, Math.ceil(length / size));
  }

  get recordCount(): number {
    return this.paginator?.length ?? this.data?.length ?? 0;
  }

  get rangeStart(): number {
    if (!this.recordCount) {
      return 0;
    }
    return this.pageIndex * (this.paginator?.pageSize || this.rows) + 1;
  }

  get rangeEnd(): number {
    if (!this.recordCount) {
      return 0;
    }
    const end = this.rangeStart + (this.paginator?.pageSize || this.rows) - 1;
    return Math.min(end, this.recordCount);
  }

  /** 1-based page for `ap-pagination`. */
  get currentPage(): number {
    return this.pageIndex + 1;
  }

  onAppkitPage(page: number): void {
    const next = Number(page);
    if (!Number.isFinite(next)) {
      return;
    }
    this.goToPage(next - 1);
  }

  goToPage(index: number): void {
    const paginator = this.paginator;
    if (!paginator || index === paginator.pageIndex || index < 0 || index >= this.pageCount) {
      return;
    }
    const previousPageIndex = paginator.pageIndex;
    paginator.pageIndex = index;
    paginator.page.emit({
      previousPageIndex,
      pageIndex: paginator.pageIndex,
      pageSize: paginator.pageSize,
      length: paginator.length,
    });
  }

  private syncTableData(): void {
    this.dataSource.data = this.data;
    this.connectTableControls();
    this.expandedElement = null;
  }

  // `width: '0px'` marks a column whose field is fetched and exported but never
  // rendered on its own, because a sibling column already prints it (Business
  // prints secondaryBusiness, Entity Name prints secondaryEntity, User Activity
  // prints created/modified by+on). A zero width collapsed these away under
  // PrimeNG, but mat-table still reserves each cell's padding, so they have to
  // be left out of the column list instead.
  private get visibleCols(): PtableColConfigModel[] {
    return this.cols.filter((c) => c.width !== '0px');
  }

  getColByField(field: string): PtableColConfigModel {
    return this.cols.find((c) => c.field === field)!;
  }

  // AppKit multi-select prints "({n}) items" unless this text is supplied.
  // The Year filter should show the selected year (or years), same as the
  // previous multi-select label.
  get selectedTaxYearLabel(): string {
    return (this.selectedTaxYearsObj || [])
      .map((item: DSTTaxYear) => item?.text)
      .filter((text) => text !== undefined && text !== null && `${text}` !== '')
      .join(', ');
  }

  getChildColHeader(field: string): string {
    return this.childCols.find((c) => c.field === field)?.header || field;
  }

  getColWidthPx(col: PtableColConfigModel): number {
    if (!col?.width) {
      return 100;
    }
    const parsed = Number.parseInt(col.width, 10);
    return Number.isNaN(parsed) ? 100 : parsed;
  }

  onColumnWidthChange(col: PtableColConfigModel, widthPx: number): void {
    col.width = `${widthPx}px`;
  }

  toggleRowExpansion(row: DSTStatement, event: Event): void {
    event.preventDefault();
    this.expandedElement = this.expandedElement === row ? null : row;
  }

  isExpansionDetailRow = (_index: number, row: DSTStatement): boolean => {
    return this.expandedElement === row;
  };

  exportToExcel(): void {
    this.exportingExcel = true;
    const exportData = this.dataSource.filteredData?.length ? this.dataSource.filteredData : this.data;
    const headersMap: JSONExportHeader[] = this.visibleCols.map((c) => ({
      header: c.header,
      field: c.field === 'userActivity' ? 'createdBy' : c.field,
    }));

    if (!exportData?.length) {
      this.dstSharedService.showAlert('warning', 'No Data found to Export');
      this.exportingExcel = false;
      return;
    }

    this.jsonExportService.exportToExcel(this.excelExportFileName, exportData, headersMap);
    this.exportingExcel = false;
  }

  ngOnDestroy(): void {
    this.onDisCancelSubs?.unsubscribe();
    this.statementFilterSubscription?.unsubscribe();
  }

  setTaxYear(taxYear: string): void {
    this.taxYear = this.taxYears.find((item: any) => { return item.text.toString() === taxYear; });
    this.taxYearItemsAll = this.taxYears.map((ty: DSTTaxYear, i: number) => ({
      value: i,
      label: ty.text.toString()
    }));
    this.taxYearItems = this.taxYearItemsAll.slice();
    this.selectedTaxYearItem = this.taxYearItems.find(i => i.label === taxYear) || this.taxYearItems[0] || {};
    this.publishDropdownOptions('taxYearColumn', this.taxYears);
    this.publishDropdownOptions('taxYearPanel', this.taxYears);
    this.taxyearLoaded = true;
  }

  private toDropdownOptions<T>(items: T[]): any[] {
    return (items || []).map((item: any) => ({
      ...item,
      // AppKit search calls toUpperCase() on the option label. Tax year
      // `text` is a number, so that call throws and the list never filters.
      label: String(item?.text ?? item?.formName ?? item?.label ?? ''),
      showCheckbox: true,
      role: 'checkbox'
    }));
  }

  private publishDropdownOptions(key: string, items: any[]): void {
    const options = this.toDropdownOptions(items);
    this.dropdownSource[key] = options;
    this.dropdownOptions[key] = options.slice();
  }

  onTaxYearFilterResult(event: any): void {
    this.taxYearItems = this.filterBySearch(this.taxYearItemsAll, event?.searchStr, 'label');
  }

  onDropdownFilterResult(key: string, event: any): void {
    const source = this.dropdownSource[key] || [];
    const field = key === 'disclosure' ? 'formName' : 'label';
    this.dropdownOptions[key] = this.filterBySearch(source, event?.searchStr, field);
  }

  private filterBySearch(source: any[], searchStr: unknown, field: string): any[] {
    const query = String(searchStr ?? '').trim().toUpperCase();
    if (!query) {
      return source.slice();
    }
    return source.filter((item: any) => String(item?.[field] ?? '').toUpperCase().includes(query));
  }

  // ap-dropdown (Appkit4) emits `event.selected: Item | Item[]` on (onSelect).
  // We translate the chosen label back to the DSTTaxYear in `this.taxYears`
  // so the rest of the logic that already operates on DSTTaxYear keeps working.
  onTaxYearChanged(event: any): void {
    const selected = Array.isArray(event?.selected) ? event.selected : (event?.selected ? [event.selected] : []);
    const change = selected[0] ?? null;
    this.selectedTaxYearItem = change || this.selectedTaxYearItem;
    const label: string = change?.label ?? change?.text ?? '';
    const matched = this.taxYears.find((ty: DSTTaxYear) => ty.text.toString() === label);
    if (matched) {
      this.taxYear = matched;
      this.selectedTaxYearsObj = [matched];
      this.dfMeDropdownComponent.loadMEsCustom(matched.text.toString());
    }
  }

  onTaxYearSelectItem(event: any): void {
    const selected = event?.selected ?? null;
    if (selected) {
      this.selectedTaxYearItem = selected;
    }
  }

  setTaxYearAriaLabel(item: any): string {
    const label = item?.label || item?.text || item?.value || '';
    return `Tax Year option ${label}`;
  }

  // Constrain the scrollable body to the available viewport space so the
  // table never pushes the paginator below the fold. The row count itself is
  // fixed (`this.rows = 8`) so we do NOT touch it here — see the comment on
  // the `rows` field for why.
  private viewportReady(): void {
    const gridContainerHeight: number = document.getElementById('cr-grid-container')?.getBoundingClientRect().height || 0;
    const toolbarEl = document.querySelector('#cr-grid-container .dst-toolbar') as HTMLElement | null;
    const topToolbarHeight: number = toolbarEl?.getBoundingClientRect().height || 42;
    const tHeadHeight: number = 90;
    const paginatorHeight: number = 60;
    const tbodyMaxHeight: number = Math.floor(gridContainerHeight - topToolbarHeight - tHeadHeight - paginatorHeight);
    if (tbodyMaxHeight < 0) {
      return;
    }
    const body = document.querySelector('#cr-grid-container .dst-mat-grid') as HTMLElement | null;
    if (body) {
      body.style.maxHeight = (tbodyMaxHeight - 1) + 'px';
    }
  }

  resetGridFilters(): void {
    this.selectedTaxYearsObj = [this.taxYear!];
    this.selectedDisclosuresObj = [];
    this.selectedTaxYears = '';
    this.selectedDisclosures = '';
    this.dfMeDropdownComponents.forEach((d: DfMeDropdownComponent) => {
      d.clear();
    });
    this.selectedFStatus = this.fStatusList[0];
    this.selectedQARStatus = this.QARStatusList[0];
    this.selectedSigFlag = this.sigElecFlagList[0];
    this.selectedElecFlag = this.sigElecFlagList[0];
    this.selectedFStatusItem = this.fStatusListItems[0];
    this.selectedQARStatusItem = this.QARStatusListItems[0];
    this.selectedSigFlagItem = this.sigElecFlagListItems[0];
    this.selectedElecFlagItem = this.sigElecFlagListItems[0];
  }

  onFStatusSelect(event: any): void {
    const selected = getAppkitDropdownSelection(event);
    this.selectedFStatusItem = selected;
    this.selectedFStatus = selected || this.fStatusList[0];
  }

  onQARStatusSelect(event: any): void {
    const selected = getAppkitDropdownSelection(event);
    this.selectedQARStatusItem = selected;
    this.selectedQARStatus = selected || this.QARStatusList[0];
  }

  onSigFlagSelect(event: any): void {
    const selected = getAppkitDropdownSelection(event);
    this.selectedSigFlagItem = selected;
    this.selectedSigFlag = selected || this.sigElecFlagList[0];
  }

  onElecFlagSelect(event: any): void {
    const selected = getAppkitDropdownSelection(event);
    this.selectedElecFlagItem = selected;
    this.selectedElecFlag = selected || this.sigElecFlagList[0];
  }

  onFilterSelectItem(event: any): void {
    getAppkitDropdownSelection(event);
  }

  setDropdownAriaLabel(item: any, contextLabel: string = 'Option'): string {
    return setDropdownAriaLabel(item, contextLabel);
  }

  private resetColFilters(): void {
    this.fStatementId = '';
    this.fFormName = '';
    this.fDisclosureType = '';
    this.fPrimaryLeid = '';
    this.fPrimaryEntity = '';
    this.fFilingGroup = '';
    this.fFilingGroupReturnType = '';
    this.fCreatedBy = '';
    this.fStatus = '';
    this.fProjectName = '';
    this.fSignatureFlag = '';
    this.fElectionFlag = '';
    this.selectedTaxYearsObj = [this.taxYear!];
  }

  resetAllFilters(): void {
    this.resetGridFilters();
    this.refresh();

  }
  onTaxYearsFilterChange(selected: DSTTaxYear[]): void {
    this.selectedTaxYears = (selected || []).map((i: DSTTaxYear) => i.text).join(',');
    this.loadMes();
  }

  onDiscFilterChange(selected: Disclosure[]): void {
    this.selectedDisclosures = (selected || []).map((d: Disclosure) => d.formId).join(',');
  }

  selectedMeFilterChanged(selected: string): void {
    this.selectedMes = selected;
    console.log(selected);
  }

  showFilters(): void {
    this.showGridFilters = true;
    setTimeout(() => {
      this.customReportFilterModal?.showModal('custom-report-filters');
    });
  }

  closeGridFilters(id: string = 'custom-report-filters'): void {
    this.customReportFilterModal?.closeModal(id);
    setTimeout(() => {
      this.showGridFilters = false;
    });
  }

  private loadMes(): void {
    this.dfMeDropdownComponents.forEach((d: DfMeDropdownComponent) => {
      d.loadMEsCustom(this.selectedTaxYears);
    });
  }

  filterByStatementId(event: Event) {

    this.statementFilter.next((event.target as HTMLInputElement).value);
  }
  refresh(): void {
    if (this.showGridFilters) {
      this.closeGridFilters();
    }
    this.data = [];
    this.dataSource.data = [];
    this.loading = true;

    this.cReportService.loadCustomReports(this.multipleTaxYears || this.taxYear!?.text.toString(), this.selectedDisclosures, this.selectedMes, this.selectedFStatus.value,
      this.selectedQARStatus.value, this.selectedSigFlag.value, this.selectedElecFlag.value, this.statementFilterCriteria).pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((data: any) => {
        try {
          data.forEach((d: any) => {
            const rowData: DSTStatement = safeJsonInput<any>(d.CUSTOMREPORTSLIST, null, 'custom-report.parse3');
            if (rowData.is304) {
              rowData.formId = -192;
            }
            this.data.push(rowData);
          });
          //console.log(this.data);
          this.table.reset();
          this.syncTableData();
          this.resetColFilters();
          this.loadMes();
        } catch (e: any) {
          if (e instanceof SyntaxError) {
            console.log('JSON parse Syntax Error' + e);
          } else {
            console.error(e);
          }
        }
        this.resetGridFilters();
        const fromMultipleTaxYears = this.taxYears.filter((ty: DSTTaxYear) => this.multipleTaxYears.split(',').includes(ty.text.toString()));
        if(fromMultipleTaxYears.length > 0){
          this.selectedTaxYearsObj = fromMultipleTaxYears;
        } else
          this.selectedTaxYearsObj = [this.taxYear!];

      }, (e: any) => {
        console.error(e);
        this.dstSharedService.showAlert('error', e);
      })
      .add(() => {
        this.loading = false;
        this.fStatementId = this.statementFilterCriteria;
      });
  }


  loadCustomFilteringData() {
    if (this.showGridFilters) {
      this.closeGridFilters();
    }
    this.data = [];
    this.dataSource.data = [];
    this.loading = true;
    this.cReportService.loadCustomReports(this.taxYear?.text.toString()!, this.selectedDisclosures, this.selectedCustomMes, this.selectedFStatus.value,
      this.selectedQARStatus.value, this.selectedSigFlag.value, this.selectedElecFlag.value, this.statementFilterCriteria).pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((data: any) => {
        try {
          data.forEach((d: any) => {
            const rowData: DSTStatement = safeJsonInput<any>(d.CUSTOMREPORTSLIST, null, 'custom-report.parse4');
            if (rowData.is304) {
              rowData.formId = -192;
            }
            this.data.push(rowData);
          });
          this.table.reset();
          this.syncTableData();
          this.resetColFilters();
          this.selectedTaxYearsObj = [this.taxYear!];
        } catch (e: any) {
          if (e instanceof SyntaxError) {
            console.log('JSON parse Syntax Error' + e);
          } else {
            console.error(e);
          }
        }
        this.multipleTaxYears = '';//empty this as we want the single tax years to be used for subsequent request unless the refresh request came from multi select dropdown.
      }, (e: any) => {
        console.error(e);
        this.dstSharedService.showAlert('error', e);
      })
      .add(() => {
        this.loading = false;
        this.fStatementId = this.statementFilterCriteria;
      });

  }

  selectedMeChanged(selected: string): void {
    const mesArray = selected.split(',');
    this.table.filter(mesArray, 'primaryMaintOwner', 'in');
  }

  selectedCustomMeChanged(selected: any): void {
    this.selectedCustomMes = selected;
    // console.log(selected);
  }

  filterCol(event: Event | { target?: { value: string } }, field: string, matchMode: string): void {
    this.table.filter((event.target as HTMLInputElement).value, field, matchMode);
  }

  onTaxYearsChange(selected: DSTTaxYear[]): void {
    const taxYears = (selected || []).map((i: DSTTaxYear) => i.text);
    this.table.filter(taxYears, 'TAXYEAR', 'in');

    setTimeout(() => {
      if (!this.table.filteredValue || this.table.filteredValue.length === 0) {
        this.multipleTaxYears = taxYears.join(',');
        this.refresh();
      }
    });
  }

  onActionClicked(payload: StmtBtnActionPayload): void {
    switch (payload.name) {
      case 'show-view-update': {
        this.activeRow = payload.data;
        this.dstSharedService.setTableActionStatus(true);
        this.dstSharedService.setIsFiled(this.activeRow.IS_FILED!);
        this.stmtIdBtnService.getChildStatements(this.activeRow.statementId).pipe(takeUntilDestroyed(this.destroyRef)).subscribe((childFormStates: DSTChildFormState[]) => {
          this.showModalViewer = true;
          setTimeout(() => {
            this.modalViewerComponent.showMore('dst-disclosure-viewer', childFormStates);
          });
        }, (eMsg: string) => {
          console.log(eMsg);
          this.dstSharedService.showAlert('error', eMsg);
        }).add(() => {
          this.dstSharedService.setTableActionStatus(false);
        });
        break;
      }
      case 'tagging-project': {
        this.activeRow = payload.data;
        this.showProjectTaggingModal = true;
        setTimeout(() => {
          this.projectTaggingComponent.showMore('project-tagging');
        });
        break;
      }
      case 'copy': {
        this.activeRow = payload.data;
        this.showCopyRepModal = true;
        this.copyMode = 'Copy';
        setTimeout(() => {
          this.copyRepComponent.showMore('copy-replicate');
        });
        break;
      }
      case 'replicate': {
        this.activeRow = payload.data;
        this.showCopyRepModal = true;
        this.copyMode = 'Replicate';
        setTimeout(() => {
          this.copyRepComponent.showMore('copy-replicate');
        });
        break;
      }
      case 'deactivate': {
        this.activeRow = payload.data;
        if (window.confirm('Are you sure you want to delete this statement?')) {
          this.loading = true;
          this.data.splice(this.data.indexOf(this.activeRow), 1);
          this.syncTableData();
          this.stmtIdBtnService.deactivateStatement(this.activeRow.statementId).pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((response: SaveActionResponse) => {
              if (response.callSuccess == 1) {
                this.dstSharedService.showAlert('success', 'Statement has been deleted successfully!');
              } else {
                this.dstSharedService.showAlert('error', 'Something went wrong while processing your request, please try again!');
              }
            }, (error: HttpErrorResponse) => {
              this.dstSharedService.showAlert('error', error.message);
            })
            .add(() => {
              this.loading = false;
            });
        }
        this.activeRow.showDropdown = false;
        break;
      }
      default:
        return;
    }
  }

  afterModalClosed(): void {
    this.trackerTabsService.destroyModalTab();
    this.showModalViewer = false;
  }

  afterProjectTaggingModalClosed(): void {
    this.showProjectTaggingModal = false;
  }

  afterCopyRepModalClosed(): void {
    this.trackerTabsService.destroyModalTab();
    this.showCopyRepModal = false;
  }

  // testData: any[] = [];
  // testCols: any[] = [{
  //   field: 'i'
  // }];
  //
  // populateTestData(): void {
  //   for (let i = 0; i < 1000; i++) {
  //     this.testData.push({i: i});
  //   }
  // }

}

interface DisclosureByTypes {
  statementTypes: string;
  children: Disclosure[];
}

interface Disclosure {
  formId: string;
  formName: string;
}

